
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ServiceType = 'timber-inventory' | 'forest-health' | 'lidar-survey' | 'pre-acquisition' | 'post-storm';
type Complexity = 'standard' | 'moderate' | 'complex';
type Frequency = 'one-time' | 'annual' | 'semi-annual';

interface ServiceOption {
  value: ServiceType;
  label: string;
  description: string;
  baseLow: number;
  baseHigh: number;
}

const serviceOptions: ServiceOption[] = [
  { value: 'timber-inventory', label: 'Timber Inventory & Stand Mapping (RGB)', description: 'Stand boundaries, density, access mapping', baseLow: 6, baseHigh: 12 },
  { value: 'forest-health', label: 'Forest Health & Stress Analysis (Multispectral)', description: 'Disease, pest, drought stress detection', baseLow: 12, baseHigh: 25 },
  { value: 'lidar-survey', label: 'LiDAR Forestry Survey', description: 'Canopy, terrain, volume, biomass modeling', baseLow: 30, baseHigh: 75 },
  { value: 'pre-acquisition', label: 'Pre-Acquisition & Valuation Survey', description: 'Independent data for buyers, sellers, funds', baseLow: 15, baseHigh: 30 },
  { value: 'post-storm', label: 'Post-Storm & Insurance Assessment', description: 'Rapid damage documentation and claims support', baseLow: 10, baseHigh: 25 },
];

const complexityOptions = [
  { value: 'standard', label: 'Standard', description: 'Flat to rolling terrain, accessible', modifier: 1.0 },
  { value: 'moderate', label: 'Moderate', description: 'Mixed terrain, partial access challenges', modifier: 1.15 },
  { value: 'complex', label: 'Complex', description: 'Dense canopy, steep terrain, limited access', modifier: 1.3 },
];

const frequencyOptions = [
  { value: 'one-time', label: 'One-Time Survey', modifier: 1.0 },
  { value: 'annual', label: 'Annual (save 10%)', modifier: 0.9 },
  { value: 'semi-annual', label: 'Semi-Annual (save 15%)', modifier: 0.85 },
];

export default function PricingCalculator() {
  const [acreage, setAcreage] = useState(500);
  const [serviceType, setServiceType] = useState<ServiceType>('timber-inventory');
  const [complexity, setComplexity] = useState<Complexity>('standard');
  const [frequency, setFrequency] = useState<Frequency>('one-time');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState(false);
  const [changeDetection, setChangeDetection] = useState(false);

  const [result, setResult] = useState({ perAcreLow: 0, perAcreHigh: 0, totalLow: 0, totalHigh: 0 });

  const getService = () => serviceOptions.find(s => s.value === serviceType) || serviceOptions[0];
  const getComplexity = () => complexityOptions.find(c => c.value === complexity) || complexityOptions[0];
  const getFrequency = () => frequencyOptions.find(f => f.value === frequency) || frequencyOptions[0];

  useEffect(() => {
    const service = getService();
    const comp = getComplexity();
    const freq = getFrequency();

    // Small-acreage scaling: at 25 acres = 1.6x, at 500 acres = 1.0x, linear between
    let multiplier = 1.0;
    if (acreage < 500) {
      const clampedAcres = Math.max(acreage, 25);
      multiplier = 1.6 - ((clampedAcres - 25) * (0.6 / 475));
    }

    let adjLow = service.baseLow * multiplier * comp.modifier;
    let adjHigh = service.baseHigh * multiplier * comp.modifier;

    // Apply frequency discount
    adjLow *= freq.modifier;
    adjHigh *= freq.modifier;

    let totalLow = adjLow * acreage;
    let totalHigh = adjHigh * acreage;

    // Add‑ons
    if (priorityTurnaround) {
      totalLow *= 1.15;
      totalHigh *= 1.15;
    }
    if (executiveSummary) {
      totalLow *= 1.20;
      totalHigh *= 1.20;
    }
    if (changeDetection) {
      totalLow *= 1.10;
      totalHigh *= 1.10;
    }

    // Per‑visit if recurring
    let annualMultiplier = 1;
    if (frequency === 'annual') annualMultiplier = 1;
    if (frequency === 'semi-annual') annualMultiplier = 2;

    const perVisitLow = totalLow;
    const perVisitHigh = totalHigh;
    const annualLow = perVisitLow * annualMultiplier;
    const annualHigh = perVisitHigh * annualMultiplier;

    setResult({
      perAcreLow: adjLow,
      perAcreHigh: adjHigh,
      totalLow: Math.round(frequency === 'one-time' ? totalLow : annualLow),
      totalHigh: Math.round(frequency === 'one-time' ? totalHigh : annualHigh),
    });
  }, [acreage, serviceType, complexity, frequency, priorityTurnaround, executiveSummary, changeDetection]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const selectedService = getService();

  return (
    <section id="calculator" className="py-20 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Forestry Project Calculator</h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Configure your project details to get an instant estimate. Adjust acreage, service type, and options to see pricing update in real time.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Inputs — 3 columns */}
          <div className="lg:col-span-3 space-y-8">
            {/* Acreage */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <label className="block text-sm font-semibold text-stone-900 mb-4">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-landscape-line text-lg text-amber-600"></i>
                  </span>
                  Total Acreage
                </span>
              </label>
              <div className="flex items-center gap-4 mb-3">
                <input
                  type="range"
                  min="25"
                  max="25000"
                  step="25"
                  value={acreage}
                  onChange={e => setAcreage(Number(e.target.value))}
                  className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                />
                <input
                  type="number"
                  min="25"
                  max="25000"
                  value={acreage}
                  onChange={e => {
                    const val = Number(e.target.value);
                    if (val >= 25 && val <= 25000) setAcreage(val);
                  }}
                  className="w-28 px-4 py-2 border border-stone-300 rounded-lg text-center font-semibold text-sm"
                />
              </div>
              <div className="flex justify-between text-xs text-stone-500">
                <span>25 acres</span>
                <span>25,000 acres</span>
              </div>
              {acreage < 500 && (
                <p className="text-xs text-amber-700 mt-3 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  Small-acreage premium applies below 500 acres
                </p>
              )}
            </div>

            {/* Service Type */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <label className="block text-sm font-semibold text-stone-900 mb-4">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-survey-line text-lg text-amber-600"></i>
                  </span>
                  Service Type
                </span>
              </label>
              <div className="space-y-3">
                {serviceOptions.map(option => (
                  <label
                    key={option.value}
                    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      serviceType === option.value ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="serviceType"
                      value={option.value}
                      checked={serviceType === option.value}
                      onChange={e => setServiceType(e.target.value as ServiceType)}
                      className="w-4 h-4 mt-0.5 cursor-pointer accent-amber-600"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-stone-900 text-sm">{option.label}</div>
                      <div className="text-xs text-stone-500 mt-0.5">{option.description}</div>
                      <div className="text-xs text-amber-700 font-medium mt-1">
                        ${option.baseLow}–${option.baseHigh}/acre base
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Complexity */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <label className="block text-sm font-semibold text-stone-900 mb-4">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mountain-line text-lg text-amber-600"></i>
                  </span>
                  Terrain &amp; Canopy Complexity
                </span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {complexityOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setComplexity(option.value as Complexity)}
                    className={`p-4 rounded-lg text-center transition-all whitespace-nowrap cursor-pointer ${
                      complexity === option.value
                        ? 'bg-amber-600 text-white shadow-md'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    <div className="font-semibold text-sm">{option.label}</div>
                    <div className={`text-xs mt-1 ${complexity === option.value ? 'text-amber-100' : 'text-stone-500'}`}>
                      {option.modifier === 1.0 ? 'Base rate' : `+${Math.round((option.modifier - 1) * 100)}%`}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <label className="block text-sm font-semibold text-stone-900 mb-4">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-calendar-schedule-line text-lg text-amber-600"></i>
                  </span>
                  Survey Frequency
                </span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {frequencyOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setFrequency(option.value as Frequency)}
                    className={`p-4 rounded-lg text-center transition-all whitespace-nowrap cursor-pointer ${
                      frequency === option.value
                        ? 'bg-amber-600 text-white shadow-md'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    <div className="font-semibold text-sm">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Add‑ons */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <label className="block text-sm font-semibold text-stone-900 mb-4">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-add-circle-line text-lg text-amber-600"></i>
                  </span>
                  Add‑ons
                </span>
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={priorityTurnaround}
                    onChange={e => setPriorityTurnaround(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-amber-600"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-semibold text-stone-900 text-sm">Priority Turnaround (72 hours)</div>
                    <div className="text-xs text-stone-500">Results delivered within 3 business days</div>
                  </div>
                  <span className="text-sm font-semibold text-amber-700">+15%</span>
                </label>

                <label className="flex items-center p-4 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={executiveSummary}
                    onChange={e => setExecutiveSummary(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-amber-600"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-semibold text-stone-900 text-sm">Executive Summary Report</div>
                    <div className="text-xs text-stone-500">Investor / buyer‑ready documentation</div>
                  </div>
                  <span className="text-sm font-semibold text-amber-700">+20%</span>
                </label>

                <label className="flex items-center p-4 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={changeDetection}
                    onChange={e => setChangeDetection(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-amber-600"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-semibold text-stone-900 text-sm">Change Detection Overlay</div>
                    <div className="text-xs text-stone-500">Compare against previous survey data</div>
                  </div>
                  <span className="text-sm font-semibold text-amber-700">+10%</span>
                </label>
              </div>
            </div>
          </div>

          {/* Results — 2 columns, sticky */}
          <div className="lg:col-span-2 lg:sticky lg:top-24 h-fit">
            <div className="bg-stone-900 rounded-xl p-8 text-white shadow-xl">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-calculator-line text-lg text-amber-400"></i>
                </span>
                Your Estimate
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center pb-3 border-b border-stone-700">
                  <span className="text-stone-400 text-sm">Acres</span>
                  <span className="text-lg font-bold">{acreage.toLocaleString()}</span>
                </div>

                <div className="flex justify-between items-center pb-3 border-b border-stone-700">
                  <span className="text-stone-400 text-sm">Service</span>
                  <span className="font-semibold text-sm text-right max-w-[180px]">{selectedService.label}</span>
                </div>

                <div className="flex justify-between items-center pb-3 border-b border-stone-700">
                  <span className="text-stone-400 text-sm">Complexity</span>
                  <span className="font-semibold text-sm capitalize">{complexity}</span>
                </div>

                <div className="flex justify-between items-center pb-3 border-b border-stone-700">
                  <span className="text-stone-400 text-sm">Frequency</span>
                  <span className="font-semibold text-sm">
                    {frequency === 'one-time' ? 'One‑Time' : frequency === 'annual' ? 'Annual' : 'Semi‑Annual'}
                  </span>
                </div>

                <div className="flex justify-between items-center pb-3 border-b border-stone-700">
                  <span className="text-stone-400 text-sm">Adjusted per‑acre rate</span>
                  <span className="font-semibold text-amber-400">
                    ${result.perAcreLow.toFixed(2)} – ${result.perAcreHigh.toFixed(2)}
                  </span>
                </div>

                {(priorityTurnaround || executiveSummary || changeDetection) && (
                  <div className="pb-3 border-b border-stone-700">
                    <span className="text-stone-400 text-sm block mb-2">Add‑ons</span>
                    <div className="space-y-1">
                      {priorityTurnaround && (
                        <div className="flex items-center gap-2 text-xs text-amber-300">
                          <i className="ri-check-line"></i> Priority turnaround +15%
                        </div>
                      )}
                      {executiveSummary && (
                        <div className="flex items-center gap-2 text-xs text-amber-300">
                          <i className="ri-check-line"></i> Executive summary +20%
                        </div>
                      )}
                      {changeDetection && (
                        <div className="flex items-center gap-2 text-xs text-amber-300">
                          <i className="ri-check-line"></i> Change detection +10%
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="bg-amber-600/20 border border-amber-500/30 rounded-lg p-6 mb-6">
                <div className="text-amber-300 text-sm mb-2 text-center">
                  {frequency === 'one-time' ? 'Estimated Project Total' : 'Estimated Annual Investment'}
                </div>
                <div className="text-3xl font-bold text-center text-white">
                  {formatCurrency(result.totalLow)} – {formatCurrency(result.totalHigh)}
                </div>
              </div>

              <p className="text-xs text-stone-400 mb-6 leading-relaxed">
                Final pricing depends on access conditions, canopy density, terrain, and deliverable scope. This estimate provides a planning baseline.
              </p>

              <div className="space-y-3">
                <Link
                  to="/instant-quote"
                  className="block w-full px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold text-center hover:bg-amber-700 transition whitespace-nowrap cursor-pointer"
                >
                  Get a Firm Quote
                </Link>
                <Link
                  to="/instant-quote"
                  className="block w-full px-6 py-3 bg-stone-800 text-white rounded-lg font-semibold text-center hover:bg-stone-700 transition border border-stone-600 whitespace-nowrap cursor-pointer"
                >
                  Schedule a Call
                </Link>
              </div>
            </div>

            {/* Quick reference */}
            <div className="mt-6 bg-white rounded-lg p-6 shadow-sm border border-stone-200">
              <h4 className="font-semibold text-stone-900 text-sm mb-3">
                <a href="#rate-table" className="hover:text-amber-700 transition-colors">
                  Base Rate Reference
                </a>
              </h4>
              <div className="space-y-2 text-xs">
                {serviceOptions.map(s => (
                  <div key={s.value} className="flex justify-between">
                    <span className="text-stone-600 truncate mr-2">{s.label.split('(')[0].trim()}</span>
                    <span className="font-semibold text-stone-900 whitespace-nowrap">
                      ${s.baseLow}–${s.baseHigh}/ac
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
